
import React, { useState, useEffect } from "react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ProfileFormValues } from "@/hooks/profile";
import UniversitySelector from "./UniversitySelector";
import LabSelector from "./LabSelector";
import UniversityForm from "./UniversityForm";
import LabForm from "./LabForm";
import { useUniversityLabData } from "./hooks/useUniversityLabData";

interface UniversityLabSelectProps {
  form: UseFormReturn<ProfileFormValues>;
}

const UniversityLabSelect: React.FC<UniversityLabSelectProps> = ({ form }) => {
  const [showUniversityForm, setShowUniversityForm] = useState(false);
  const [showLabForm, setShowLabForm] = useState(false);
  
  // Initialize with the university ID from the form
  const initialUniversityId = form.getValues().university_id;
  
  const {
    universities,
    labs,
    selectedUniversity,
    setSelectedUniversity,
    updateUniversities,
    updateLabs
  } = useUniversityLabData(initialUniversityId);
  
  // When university changes in the form, update the selected university
  useEffect(() => {
    const universityId = form.watch("university_id");
    if (universityId && universityId !== selectedUniversity) {
      setSelectedUniversity(universityId);
    }
  }, [form.watch("university_id"), selectedUniversity, setSelectedUniversity]);
  
  const handleUniversityChange = (value: string) => {
    // Handle "add new" option
    if (value === "add_new") {
      setShowUniversityForm(true);
      return;
    }
    
    // Set the university ID and find the university name
    form.setValue("university_id", value);
    
    const selectedUniv = universities.find(u => u.id === value);
    if (selectedUniv) {
      form.setValue("university", selectedUniv.name);
    }
    
    // Clear lab values when university changes
    form.setValue("lab_id", "");
    form.setValue("lab", "");
    
    // Update the selected university for labs filtering
    setSelectedUniversity(value);
  };
  
  const handleLabChange = (value: string) => {
    // Handle "add new" option
    if (value === "add_new") {
      setShowLabForm(true);
      return;
    }
    
    // Set the lab ID and find the lab name
    form.setValue("lab_id", value);
    
    const selectedLab = labs.find(l => l.id === value);
    if (selectedLab) {
      form.setValue("lab", selectedLab.name);
    }
  };
  
  const handleUniversityFormSubmit = (university: { name: string; address: string; city: string; postcode: string; country: string; id: string }) => {
    // Add the new university to the list
    updateUniversities(university);
    
    // Set form values
    form.setValue("university_id", university.id);
    form.setValue("university", university.name);
    
    // Update selected university for labs filtering
    setSelectedUniversity(university.id);
    
    // Hide the form
    setShowUniversityForm(false);
  };
  
  const handleLabFormCancel = () => {
    setShowLabForm(false);
  };
  
  const handleUniversityFormCancel = () => {
    setShowUniversityForm(false);
  };
  
  const handleLabFormSubmit = (lab: { name: string; id: string; university_id: string }) => {
    // Add the new lab to the list
    updateLabs(lab);
    
    // Set form values
    form.setValue("lab_id", lab.id);
    form.setValue("lab", lab.name);
    
    // Hide the form
    setShowLabForm(false);
  };
  
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <FormField
        control={form.control}
        name="university_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>University</FormLabel>
            <FormControl>
              {showUniversityForm ? (
                <UniversityForm 
                  isOpen={showUniversityForm}
                  onClose={handleUniversityFormCancel}
                  onSuccess={handleUniversityFormSubmit}
                />
              ) : (
                <UniversitySelector
                  universities={universities}
                  value={field.value || ""}
                  onChange={handleUniversityChange}
                  onAddNew={() => setShowUniversityForm(true)}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="lab_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lab</FormLabel>
            <FormControl>
              {showLabForm ? (
                <LabForm 
                  isOpen={showLabForm}
                  onClose={handleLabFormCancel}
                  onSuccess={handleLabFormSubmit}
                  universityId={selectedUniversity}
                />
              ) : (
                <LabSelector
                  labs={labs}
                  value={field.value || ""}
                  onChange={handleLabChange}
                  disabled={!selectedUniversity}
                  universitySelected={!!selectedUniversity}
                  onAddNew={() => setShowLabForm(true)}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default UniversityLabSelect;
